import React from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { Banner } from '../../ui/Banner/Banner';
import { Button } from '../../ui/Button/Button';
import type { BridgeCause } from '../../utils/bridge/bridgeCause';
import { CopyDiagnosticsButton } from './CopyDiagnosticsButton';

interface BridgeCauseBannerProps {
    cause: BridgeCause;
    onRetry?: () => void;
    retrying?: boolean;
    /** Icon-on-top, centered layout for a full-page state — see Banner's own `centered` prop. */
    centered?: boolean;
}

/**
 * One message per cause. "Make sure the service is running" is wrong for most of these and impossible
 * for a per-user install, which has no service to start.
 */
const COPY: Record<Exclude<BridgeCause, 'connected'>, { title: string; body: string; tone: 'warning' | 'danger' }> = {
    absent: {
        title: TranslationKey.AcombaBridge.Cause.AbsentTitle,
        body: TranslationKey.AcombaBridge.Cause.AbsentBody,
        tone: 'warning',
    },
    stopped: {
        title: TranslationKey.AcombaBridge.Cause.StoppedTitle,
        body: TranslationKey.AcombaBridge.Cause.StoppedBody,
        tone: 'warning',
    },
    stale: {
        title: TranslationKey.AcombaBridge.Cause.StaleTitle,
        body: TranslationKey.AcombaBridge.Cause.StaleBody,
        tone: 'warning',
    },
    unhealthy: {
        title: TranslationKey.AcombaBridge.Cause.UnhealthyTitle,
        body: TranslationKey.AcombaBridge.Cause.UnhealthyBody,
        tone: 'danger',
    },
    unreachable: {
        title: TranslationKey.AcombaBridge.Cause.UnreachableTitle,
        body: TranslationKey.AcombaBridge.Cause.UnreachableBody,
        tone: 'danger',
    },
};

export const BridgeCauseBanner: React.FC<BridgeCauseBannerProps> = ({ cause, onRetry, retrying, centered }) => {
    const { t } = useTranslation();

    if (cause === 'connected') return null;

    const copy = COPY[cause];

    return (
        <Banner
            tone={copy.tone}
            testId={`acomba-bridge-cause-${cause}`}
            title={t(copy.title)}
            centered={centered}
        >
            {t(copy.body)}
            <div className={`mt-m flex flex-col ${centered ? 'items-center' : 'items-start'} gap-m`}>
                {onRetry && (
                    <Button
                        variant="secondary"
                        type="button"
                        data-testid="acomba-bridge-cause-retry-btn"
                        onClick={onRetry}
                        disabled={retrying}
                    >
                        {t(TranslationKey.AcombaBridge.Wizard.RetryBtn)}
                    </Button>
                )}
                <CopyDiagnosticsButton cause={cause} />
            </div>
        </Banner>
    );
};
